import {Country, User} from "models"
import {memo, shapeComponent, ShapeComponent, useI18n, View} from "shared/base"
import AppHistory from "shared/history"
import Button from "components/chalky-layout/button"
import ChalkyCheckbox from "components/chalky-layout/checkbox"
import ChalkyInput from "components/chalky-layout/input"
import Devise from "@kaspernj/api-maker/build/devise"
import {digg} from "diggerize"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import React from "react"
import {StyleSheet} from "react-native"
import updateSession from "components/users/update-session"

const styles = StyleSheet.create({
  rootContainer: {
    backgroundColor: "#ffffff",
    minHeight: "100vh",
    paddingTop: 32,
    paddingBottom: 32,
    paddingHorizontal: 16
  },
  formContainer: {
    maxWidth: 520,
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto"
  },
  field: {
    marginBottom: 16
  },
  acceptTerms: {
    marginTop: 8,
    marginBottom: 16
  },
  buttonRow: {
    marginTop: 16
  }
})

/**
 * @typedef {object} RoutesEmbedSignUpProps
 */

/**
 * @typedef {object} RoutesEmbedSignUpState
 * @property {boolean} acceptTerms
 * @property {Country | null} swedenCountry
 * @property {boolean} submitting
 * @property {object | null} form
 */

/**
 * @extends {ShapeComponent<RoutesEmbedSignUpProps, RoutesEmbedSignUpState>}
 */
class RoutesEmbedSignUp extends ShapeComponent {
  state = {
    acceptTerms: false,
    swedenCountry: null,
    submitting: false,
    form: null
  }

  componentDidMount() {
    this.loadSwedenCountry()
  }

  loadSwedenCountry = async () => {
    const country = await Country.ransack({country_code_eq: "SE"}).first()

    this.setState({swedenCountry: country})
  }

  render() {
    const {t} = useI18n({namespace: "js.routes.embed.sign_up"})

    return (
      <View dataSet={this.rootDataSet ||= {class: "routes--embed--sign-up"}} style={styles.rootContainer}>
        <View style={styles.formContainer}>
          <Form onSubmit={this.tt.onSubmit} setForm={this.setStates.form}>
            <View style={styles.field}>
              <ChalkyInput
                dataSet={this.firstNameDataSet ||= {class: "first-name-input"}}
                label={t(".first_name")}
                name="user[contact_attributes][first_name]"
              />
            </View>
            <View style={styles.field}>
              <ChalkyInput
                dataSet={this.lastNameDataSet ||= {class: "last-name-input"}}
                label={t(".last_name")}
                name="user[contact_attributes][last_name]"
              />
            </View>
            <View style={styles.field}>
              <ChalkyInput
                dataSet={this.emailDataSet ||= {class: "email-input"}}
                label={t(".email")}
                name="user[email]"
              />
            </View>
            <View style={styles.field}>
              <ChalkyInput
                dataSet={this.emailConfirmationDataSet ||= {class: "email-confirmation-input"}}
                label={t(".repeat_email")}
                name="user[email_confirmation]"
              />
            </View>
            <View style={styles.field}>
              <ChalkyInput
                dataSet={this.passwordDataSet ||= {class: "password-input"}}
                label={t(".password")}
                name="user[password]"
                secureTextEntry
              />
            </View>
            <View style={styles.field}>
              <ChalkyInput
                dataSet={this.passwordConfirmationDataSet ||= {class: "password-confirmation-input"}}
                label={t(".repeat_password")}
                name="user[password_confirmation]"
                secureTextEntry
              />
            </View>
            <View style={styles.field}>
              <ChalkyInput
                dataSet={this.schoolNameDataSet ||= {class: "school-name-input"}}
                label={t(".school_name")}
                name="new_school[name]"
              />
            </View>
            <View style={styles.acceptTerms}>
              <ChalkyCheckbox
                checked={this.s.acceptTerms}
                dataSet={this.acceptTermsDataSet ||= {class: "accept-terms"}}
                label={t(".accept_terms")}
                name="user[notification_setting_attributes][accept_terms]"
                onChangeChecked={this.setStates.acceptTerms}
              />
            </View>
            <View style={styles.buttonRow}>
              <Button
                dataSet={this.submitDataSet ||= {class: "submit-button"}}
                disabled={this.s.submitting || !this.s.acceptTerms || !this.s.swedenCountry}
                onPress={this.tt.onSubmit}
                primary
                title={t(".create_account")}
              />
            </View>
          </Form>
        </View>
      </View>
    )
  }

  onSubmit = async () => {
    if (this.s.submitting || !this.s.swedenCountry || !this.s.form) return

    this.setState({submitting: true})

    try {
      const data = this.s.form.asObject()

      data.user ||= {}
      data.user.contact_attributes ||= {}
      data.user.contact_attributes.country_id = this.s.swedenCountry.id()

      const result = await User.createUser(data)
      const user = digg(result, "user")

      await Devise.persistSession()
      await updateSession(user.id())

      AppHistory.push("/embed/se/skapa-konto/valkommen")
    } catch (error) {
      FlashMessage.errorResponse(error)
    } finally {
      this.setState({submitting: false})
    }
  }
}

export default memo(shapeComponent(RoutesEmbedSignUp))
